import type { FC } from 'react';
import type { FetchStatus } from '../lib/types';
import { s } from '../lib/styles';
import StatusRow from './StatusRow';

const EXAMPLES: string[] = [
  '30×20×15cm 3-ply 500 units Mumbai',
  '40×30×20 5-ply Kraft 150gsm 1000 units Delhi',
  '25×25×25cm B-flute 7-ply Bangalore',
];

interface Props {
  vendorStatus: FetchStatus;
  vendorError: string | null;
  onRetryVendors: () => void;
  vendorCount: number;
  description: string;
  onDescriptionChange: (value: string) => void;
  parseError: string | null;
  parsing: boolean;
  onParse: () => void;
}

const Page1_DataAndDescription: FC<Props> = ({
  vendorStatus,
  vendorError,
  onRetryVendors,
  vendorCount,
  description,
  onDescriptionChange,
  parseError,
  parsing,
  onParse,
}) => {
  const dataReady = vendorStatus === 'success';
  const canParse = dataReady && description.trim().length > 0 && !parsing;

  return (
    <div style={s.body}>
      {/* Vendor status */}
      <div style={s.card}>
        <div style={{ ...s.cardTitle, marginBottom: 10 }}>
          Live Data Sources
        </div>
        <StatusRow
          label="Vendor List"
          status={vendorStatus}
          count={vendorCount}
          error={vendorError}
          onRetry={onRetryVendors}
        />
        <div
          style={{
            marginTop: 8,
            fontSize: 10,
            color: '#94a3b8',
            lineHeight: 1.6,
          }}
        >
          Each vendor supplies their own{' '}
          <strong style={{ color: '#475569' }}>paper rate</strong> and{' '}
          <strong style={{ color: '#475569' }}>conversion cost</strong>.
        </div>
      </div>

      {/* Description textarea */}
      <div style={s.card}>
        <div style={s.cardTitle}>Box Description</div>
        <p
          style={{
            fontSize: 11,
            color: '#94a3b8',
            margin: '5px 0 11px',
            lineHeight: 1.65,
          }}
        >
          Describe your box in plain English — dimensions, ply structure,
          materials, quantity, and delivery location. AI will extract all fields
          automatically.
        </p>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={`e.g. "500 units of 30×20×15cm 5-ply box, 150 GSM Kraft liners, C-flute at 120 GSM, delivered to Pune"`}
          style={s.textarea}
          rows={4}
        />
        {parseError && (
          <div style={{ ...s.inlineError, marginTop: 8 }}>⚠ {parseError}</div>
        )}
      </div>

      <button
        onClick={onParse}
        disabled={!canParse}
        style={{ ...s.primaryBtn, opacity: canParse ? 1 : 0.55 }}
      >
        {parsing ? (
          <span style={s.btnRow}>
            <span style={s.spinner} /> Parsing...
          </span>
        ) : (
          <span style={s.btnRow}>Parse &amp; Continue →</span>
        )}
      </button>

      {!dataReady && (
        <div
          style={{
            fontSize: 11,
            color: '#94a3b8',
            textAlign: 'center',
            marginTop: -4,
          }}
        >
          Waiting for vendor data to load.
        </div>
      )}
    </div>
  );
};

export default Page1_DataAndDescription;
